// The staff editor behind the configurator: where a measure is placed, in what
// order, under which class chip. What a measure *is* — wording, price, rules,
// pictures — belongs to CATALOGUE; the tree this produces reads through
// CONFIGURATOR.
import type {
  ConfiguratorGroupKind,
  ConfiguratorPropertyType,
} from '../../enums'
import type { FormulaNameKind } from '../../utils/evaluate-formula'
import type {
  FORMULA_CONSTANTS,
  FORMULA_KEYWORDS,
} from '../../utils/formula-tokens'

import { COMMON } from './common'
import { COST_LABELS } from './costs'
import { TERMS } from './terms'

const RESULT_PREFIX = 'Ergebnis:'

/**
 * The three kinds of node the tree is built from.
 *
 * A plain group carries questions and further groups; a rung sets the scope
 * everything under it is asked at, and is named after what it stands for
 * rather than given a name of its own.
 *
 * Here rather than in `enum-labels.ts` with the other enum tables, because
 * that file reads `CONFIGURATOR_CMS` and the two cannot import each other.
 * `satisfies` still holds: a kind added to `schema.prisma` fails the build
 * until it has a word.
 */
export const GROUP_KIND_LABELS = {
  GROUP: TERMS.group,
  FLOOR: TERMS.floor,
  ROOM: TERMS.room,
} as const satisfies Record<ConfiguratorGroupKind, string>

export const CONFIGURATOR_CMS = {
  media: {
    searchImages: 'Bilder suchen …',
    imageTitlePlaceholder: 'Bildtitel …',
    tags: 'Tags',
    deleteImage: 'Bild löschen',
    deleteImageConfirm: 'Möchtest du dieses Bild wirklich löschen?',
    pickOne: 'Bild auswählen',
    pickMany: 'Bilder auswählen',
    apply: COMMON.apply,
    applySelected: (n: number) => `${n} ausgewählt — Übernehmen`,
  },
  formulas: {
    newFormula: 'Neue Formel',
    addCode: 'Ausdruck hinterlegen',
    functionName: 'Funktionsname',
    displayName: 'Anzeigename',
    formula: TERMS.formula,
    delete: 'Formel löschen',
    deleteConfirm: 'Möchtest du diese Formel wirklich löschen?',
    possibleValues: (values: string) => `Mögliche Werte: ${values}`,
    /** The detail beside a code whose measure hangs in no question. The name
     *  resolves — it reads 0, because nobody can pick it. */
    unplacedOptionCode: 'In keiner Frage platziert',
    /** What a name in a formula refers to, as the heading over its group — in
     *  the editor's completion popup and in its reference panel. */
    kinds: {
      PROPERTY: TERMS.property,
      ROOM: TERMS.room,
      FLOOR: TERMS.floor,
      ELEMENT_TYPE: TERMS.elementTypePlural,
      OPTION_CODE: 'Gewählte Maßnahmen',
      LIBRARY: TERMS.formulaPlural,
      FUNCTION: 'Funktionen',
      STAGE: 'Zwischensummen',
    } satisfies Record<FormulaNameKind, string>,
    /** Said beside a function in the reference, where its name is not the
     *  whole story. */
    functionInfo: {
      if: 'Die nicht gewählte Hälfte wird nicht berechnet — damit lässt sich ein leeres Feld umgehen.',
      isset: 'Ergibt 1, wenn das Feld im Projekt gefüllt ist, sonst 0.',
      mod: 'Rest einer Division.',
    },
    /** Parameter names in a signature the popup shows, where the signature
     *  names anything but a bare `x`. */
    signatures: {
      if: 'if(bedingung, dann, sonst)',
      pow: 'pow(basis, exponent)',
    },
    /**
     * What each word the language owns does, and what each value it names is.
     *
     * Exhaustive over the scanner's own lists, so a word added there is a
     * build error until the editor can say what it means.
     */
    keyword: 'Schlüsselwort',
    constant: 'Fester Wert',
    keywordInfo: {
      and: 'Beide Seiten müssen wahr sein. Als Wort geschrieben, nicht als && oder ||.',
      or: 'Eine der beiden Seiten muss wahr sein.',
      not: 'Kehrt wahr und falsch um.',
      in: 'Prüft, ob ein Wert in einer Aufzählung vorkommt.',
      to: 'Rechnet eine Einheit in eine andere um.',
    } satisfies Record<(typeof FORMULA_KEYWORDS)[number], string>,
    constantInfo: {
      true: 'Wahr.',
      false: 'Falsch.',
      null: 'Kein Wert — mit isset() abfragbar.',
    } satisfies Record<(typeof FORMULA_CONSTANTS)[number], string>,
    /** The dialog warns before it drops an expression somebody typed. */
    unsaved: {
      message: 'Die Formel wurde geändert, aber noch nicht gespeichert.',
      /** The draft cannot be saved as it stands, so the question has two
       *  answers rather than three. */
      brokenMessage:
        'Die Formel wurde geändert, lässt sich aber nicht lesen und deshalb nicht speichern. Du kannst die Änderung verwerfen oder zurück zum Bearbeiten.',
    },
    reference: 'Variablen',
    referenceSearch: 'Variable suchen …',
    referenceEmpty: 'Keine Variable passt dazu.',
    pickTestProject: 'Projekt wählen …',
    searchTestProject: 'Projekt suchen …',
    projectContextFailed: 'Projekt-Kontext konnte nicht geladen werden.',
    result: RESULT_PREFIX,
    pickTestProjectFirst: 'Bitte Test-Projekt wählen',
    loadingProjectContext: 'Lade Projekt-Kontext …',
    testContext: 'Test-Kontext:',
    codeEmpty: 'Formel ist leer',
    syntaxErrorShort: 'Syntaxfehler',
    onRequest: COST_LABELS.priceOnRequest,
    hideDiagnostics: 'Diagnose ausblenden',
    whyLink: 'Warum?',
    diagnostics: 'Diagnose',
    detailSuffix: (detail: string) => `(Details: ${detail})`,
    failingSubFormulas: 'Sub-Formeln die scheitern:',
    scopeValues: 'Werte im Scope (relevant):',
    headlineSubFormulaNull: (name: string, variable: string) =>
      `Sub-Formel "${name}" scheitert weil Variable "${variable}" null ist im Test-Kontext.`,
    headlineSyntaxError: (message: string) => `Syntax-Fehler: ${message}`,
    headlineRuntimeError: (message: string) => `Laufzeit-Fehler: ${message}`,
    headlineNonFinite: (raw: string) =>
      `Ergebnis ist keine endliche Zahl (${raw})`,
    headlineNullVariable: (variable: string) =>
      `Variable "${variable}" ist null im Test-Kontext und die Formel hat keinen isset()-Guard darauf.`,
    headlineOk: (value: string) => `${RESULT_PREFIX} ${value}`,
    subNullWithDetail: (variable: string, detail: string) =>
      `Variable "${variable}" ist null (Details: ${detail})`,
    subNull: (variable: string) => `Variable "${variable}" ist null`,
    subError: (message: string) => `Fehler: ${message}`,
    hintCheckSyntax:
      'Prüfe Klammern, Operatoren und Variable-Namen (case-sensitive).',
    hintSwitchProjectOrIsset: (variable: string) =>
      `Vorschlag: Test-Projekt wechseln (falls die Variable dort einen Wert hat) oder isset() einbauen: if(isset(${variable}), formel, 0)`,
    hintSubFormulaIsset: (name: string, variable: string) =>
      `Vorschlag: Test-Projekt wechseln oder in Sub-Formel "${name}" isset() einbauen: if(isset(${variable}), formel, 0)`,
  },
  placementIn: (question: string) => `Platzierung in „${question}“`,
  editMeasure: (measure: string) => `${measure} bearbeiten`,
  removePlacement: `Aus der ${TERMS.question} entfernen`,
  removePlacementConfirm: `Die ${TERMS.measure} wird aus dieser ${TERMS.question} entfernt. Die ${TERMS.measure} selbst bleibt im Katalog.`,
  addMeasure: 'Maßnahme platzieren',
  placeMeasures: (n: number) =>
    `${n} ${n === 1 ? TERMS.measure : TERMS.measurePlural} platzieren`,
  preselected: 'Vorausgewählt',
  priceFixed: 'Fester Preis',
  expandEditor: 'Vergrößern',
  uploadZoneLabel: 'Bilder hierher ziehen oder klicken',
  uploadZoneSublabel: 'JPG, PNG oder WebP',
  noData: 'Noch keine Konfigurator-Daten vorhanden',
  defaultGroupName: `Neue ${TERMS.group}`,
  // Test-context picker on the formula editor.
  testProjectFallback: (id: number) => `Projekt #${id}`,
  testScopeWholeObject: `${TERMS.building} (ohne Fokus)`,
  formulaBodyPlaceholder: 'livingArea * 25 + 2000',
  addRootGroup: `${TERMS.group} hinzufügen`,
  addQuestion: `Neue ${TERMS.question}`,
  formulaDeleteReferencedError: (count: number) =>
    count === 1
      ? `Diese Formel wird noch von einer ${TERMS.measure} verwendet.`
      : `Diese Formel wird noch von ${count} ${TERMS.measurePlural} verwendet.`,
  measureAlreadyPlacedError: `Diese ${TERMS.measure} ist in dieser ${TERMS.question} bereits platziert.`,
  // The cycle comes from the formula engine and stays English, as IT
  // vocabulary does — it names the chain of formulas that closes on itself.
  formulaCircularError: (cycle: string) =>
    `Zirkuläre Formel-Referenz: ${cycle}`,
  questionStillPlacedError: (count: number) =>
    count === 1
      ? `Diese ${TERMS.question} ist noch einer ${TERMS.group} zugeordnet.`
      : `Diese ${TERMS.question} ist noch ${count} ${TERMS.groupPlural} zugeordnet.`,
  questionSeveralDefaultsError: (count: number) =>
    `Bei einer Einfachauswahl ist nur eine Maßnahme vorausgewählt. Diese ${TERMS.question} hat ${count} – nimm die Vorauswahl erst bei allen bis auf eine zurück.`,
  formulaProtectedError: (name: string) =>
    `Die Formel „${name}“ wird von der Anwendung gelesen und ist gegen Änderungen geschützt.`,
  mediaDeleteReferencedError: (count: number) =>
    count === 1
      ? `Dieses Bild wird noch von einer ${TERMS.measure} verwendet.`
      : `Dieses Bild wird noch von ${count} ${TERMS.measurePlural} verwendet.`,
  formulaProtectedNameHint:
    'Wird von der Anwendung unter diesem Namen gelesen und kann daher nicht umbenannt oder gelöscht werden.',
  propertiesModalTitle: (name: string) =>
    `${TERMS.characteristicPlural} — ${name}`,
  propertiesNone: `Keine ${TERMS.characteristicPlural} vorhanden`,
  displayedPropertiesHint: `Diese Eigenschaften vergleichen die Karten dieser ${TERMS.question}, in dieser Reihenfolge. Zur Auswahl stehen die Eigenschaften der hier platzierten ${TERMS.measurePlural}.`,
  availablePropertyKeys: 'Verfügbar',
  noAvailablePropertyKeys:
    'Die platzierten Maßnahmen haben keine weiteren Eigenschaften.',
  questionUnlink: 'Zuordnung aufheben',
  questionUnlinkConfirm: (group: string) =>
    `Die ${TERMS.question} aus „${group}“ herausnehmen?`,
  /** The question survives, so the warning is about where it ends up: in the
   *  catalogue, held by nothing, which is the one place it is easy to lose. */
  questionUnlinkLastConfirm: `Das ist die letzte Zuordnung — die ${TERMS.question} steht danach unter „Ohne Zuordnung“.`,
  /** Named by what the node is: a rung reads as its kind everywhere else too. */
  deleteGroup: (kind: ConfiguratorGroupKind) =>
    `${GROUP_KIND_LABELS[kind]} löschen`,
  deleteGroupConfirm: (kind: ConfiguratorGroupKind) =>
    `${GROUP_KIND_LABELS[kind]}, alle Untergruppen und ihre ${TERMS.questionPlural}-Zuordnungen löschen? Die ${TERMS.questionPlural} und ${TERMS.measurePlural} selbst bleiben erhalten.`,
  questionAssign: `${TERMS.question} zuordnen…`,
  questionSearch: `${TERMS.question} oder ${TERMS.group} suchen…`,
  questionCreate: 'Neue erstellen',
  /** Says what the rows are for: the drag is the only way to put a question at
   *  a chosen spot rather than at the end of a block. */
  unplacedHint: 'In den Baum ziehen, um sie zu platzieren',
  catalogueSearch: `${TERMS.question} suchen…`,
  /** The cut the list offers. "Alle" is the toolbar's own word, in
   *  `CONTROLS.filter`, because every filtered list says it. */
  placementFilter: {
    placed: 'Zugeordnet',
    unplaced: 'Ohne Zuordnung',
  },
  questionsUnit: TERMS.questionPlural,
  customerPane: 'Kundensicht',
  /** The customer preview needs a property to resolve against; without one
   *  there are no storeys and no rooms to show. */
  customerPaneNoProject: 'Kein Musterprojekt gewählt',
  customerPanePick: 'Musterprojekt wählen …',
  customerPaneHint:
    'So läuft der Kunde durch den Baum. Fragen, die weiter oben beantwortet werden und änderbar sind, tauchen hier noch einmal auf.',
  /** The three things a node can hold, as the row menu offers them. */
  addGroupKind: (kind: string) => `${kind} hinzufügen`,
  /** What each kind of node is called where one is added or named. */
  groupKinds: GROUP_KIND_LABELS,
  /** What a node is called wherever one is printed. One rule, so a storey or
   *  room group cannot read as a nameless row on one surface and as its kind
   *  on another. */
  groupName: (group: { name: string | null; kind: ConfiguratorGroupKind }) =>
    group.name ?? GROUP_KIND_LABELS[group.kind],
  /** What a rung covers: every storey or room, or only the types picked. */
  covers: 'Gilt für',
  coversAll: 'Alle',
  /** Two rungs of one kind with the same types read as the same row: a rung is
   *  named after the place it lands on, not after itself. */
  duplicateCoverage: 'Gibt es hier zweimal — grenze eine der beiden ein.',
  coversHelper: {
    FLOOR: 'Nichts gewählt = alle Geschosse des Objekts.',
    ROOM: 'Nichts gewählt = alle Räume des Geschosses darüber.',
  },
  occurrencesNone: 'Trifft im Musterprojekt auf nichts zu',

  // ── What a question does, read off where it hangs ──
  /** Nothing here is a setting: the tree says it, the pane states it. */
  rule: 'Wirkung',
  positionAt: 'Position',
  /** Two levels: the deeper one may depart from the answer given above. */
  mayDepart: (deeper: string, above: string) =>
    `Jeder ${deeper} darf von der Antwort auf ${above}-Ebene abweichen.`,
  bindsBelow: 'Die Antwort gilt bindend für alles darunter.',

  // ── Rules the server enforces ──
  levelNeedsCustomerScope: `${TERMS.floor} und ${TERMS.room} gibt es nur im Kunden-Scope — ein öffentlicher Baum hat kein ${TERMS.characteristic}, gegen das er aufgelöst werden könnte.`,
  groupKindPlacement: `Ein ${TERMS.floor} hängt nur direkt in einem Kapitel, ein ${TERMS.room} nur direkt in einem ${TERMS.floor}.`,
  groupCycle: `Eine ${TERMS.group} kann nicht in sich selbst oder in eine ihrer Untergruppen verschoben werden.`,
  questionScopeMismatch: `Die ${TERMS.question} und die ${TERMS.group} müssen denselben Scope haben.`,
  summaryValueMapInvalid: (max: number, labelMax: number) =>
    `Die Wertzuordnung darf höchstens ${max} Zahlen enthalten, jeweils mit einer Bezeichnung von höchstens ${labelMax} Zeichen.`,
  /** What a measure property holds. */
  propertyValueTypes: {
    TEXT: 'Text',
    NUMBER: 'Zahl',
  } as const satisfies Record<ConfiguratorPropertyType, string>,
  questionsCount: (n: number) =>
    n === 1 ? `1 ${TERMS.question}` : `${n} ${TERMS.questionPlural}`,
  /** The fixed row above the tree: every question, wherever it hangs. */
  allQuestions: `Alle ${TERMS.questionPlural}`,
  unplacedCount: (n: number) => `${n} ohne Zuordnung`,
  unplacedNone: `Jede ${TERMS.question} ist mindestens einer ${TERMS.group} zugeordnet.`,
  usedInNowhere: `Noch keiner ${TERMS.group} zugeordnet`,
  usedInCount: (n: number) =>
    n === 1 ? `In 1 ${TERMS.group}` : `In ${n} ${TERMS.groupPlural}`,
  /** The same fact in the width of a tree row. */
  usedInBadge: (n: number) => `×${n}`,
  settings: 'Einstellungen',
  noPlacements: 'Noch keine Maßnahme platziert',
  deleteQuestionConfirm: `Die ${TERMS.question} wird gelöscht. Die darin platzierten ${TERMS.measurePlural} bleiben im Katalog.`,
  displayedProperties: 'Vergleichswerte',
  /** Columns of the question table that name no entity field. */
  fields: {
    selectionType: COMMON.type,
    optionCount: 'Optionen',
    usedIn: COMMON.usedIn,
  },
  groupsUnit: TERMS.groupPlural,
  groupsCount: (n: number) => `${n} ${TERMS.groupPlural}`,
  propertiesCount: (n: number) => `${n} Eigensch.`,
} as const
